import { createSignal } from 'solid-js';

import type { FxDraftLayer } from './fx-draft-layer.types';
import type { FxBreakline } from './fx-workspace-breakline.types';

interface FxCustomDraftLayer {
  readonly layerId: string;
  readonly name: string;
}

export interface FxWorkspaceDraftLayersApi {
  createDraftLayer(workspaceId: string, name: string): void;
  listDraftLayers(workspaceId: string): readonly FxDraftLayer[];
  clearWorkspace(workspaceId: string): void;
  reset(): void;
}

export function useFxWorkspaceDraftLayers(options: {
  listWorkspaceBreaklines(workspaceId: string): readonly FxBreakline[];
}): FxWorkspaceDraftLayersApi {
  const [customLayers, setCustomLayers] = createSignal<
    Record<string, readonly FxCustomDraftLayer[]>
  >({});
  let customLayerCounter = 0;

  // TODO(noissue): replace local creation with Maps createLayer(projectId, {...})
  // so the layer is persisted and bound to the workspace via MapLayer.workspaceId.
  const createDraftLayer = (workspaceId: string, name: string): void => {
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      return;
    }

    customLayerCounter += 1;
    const layer: FxCustomDraftLayer = {
      layerId: `custom-${workspaceId}-${customLayerCounter}`,
      name: trimmed,
    };

    setCustomLayers((previous) => ({
      ...previous,
      [workspaceId]: [...(previous[workspaceId] ?? []), layer],
    }));
  };

  const listDraftLayers = (workspaceId: string): readonly FxDraftLayer[] => {
    const breaklineLayers: FxDraftLayer[] = options
      .listWorkspaceBreaklines(workspaceId)
      .map((breakline) => ({
        layerId: breakline.layerId,
        label: breakline.name,
        kind: 'breakline',
        breaklineType: breakline.type,
      }));

    const custom: FxDraftLayer[] = (customLayers()[workspaceId] ?? []).map(
      (layer) => ({
        layerId: layer.layerId,
        label: layer.name,
        kind: 'custom',
      })
    );

    return [...breaklineLayers, ...custom];
  };

  const clearWorkspace = (workspaceId: string): void => {
    setCustomLayers((previous) => {
      if (previous[workspaceId] === undefined) {
        return previous;
      }

      const next = { ...previous };
      delete next[workspaceId];
      return next;
    });
  };

  const reset = (): void => {
    setCustomLayers({});
    customLayerCounter = 0;
  };

  return {
    createDraftLayer,
    listDraftLayers,
    clearWorkspace,
    reset,
  };
}
